/* UKOO layer: motion kit foundation. Small, dependency-free, transform/opacity only. */
(() => {
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const setStatic = () => root.classList.toggle('ukoo-static', reduce.matches);
  setStatic();
  reduce.addEventListener?.('change', setStatic);
  root.classList.add('ukoo-js');

  /* Reveal on scroll: any [data-ukoo-reveal] or .ukoo-rv element fades up once. */
  const reveal = () => {
    const els = document.querySelectorAll('[data-ukoo-reveal]:not(.is-in), .ukoo-rv:not(.is-in), .uk-rv:not(.is-in)');
    if (!els.length) return;
    if (reduce.matches || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
  };

  /* Header: scrolled state, and hide on scroll down / show on scroll up. */
  const header = () => {
    const wrap = document.querySelector('.section-site-header');
    if (!wrap) return;
    let lastY = window.scrollY;
    let ticking = false;
    const search = wrap.querySelector('[data-header-search]');
    const busy = () =>
      wrap.querySelector('.header-is-active') ||
      (search && search.getClientRects().length > 0) ||
      document.body.style.overflow === 'hidden';
    /* the bar above the header and the header's height are measured only when they resize, never per scroll frame */
    let top = 0;
    let hh = 0;
    const measure = () => {
      const prev = wrap.previousElementSibling;
      top = prev ? prev.getBoundingClientRect().bottom + window.scrollY : 0;
      hh = wrap.offsetHeight;
    };
    measure();
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(measure);
      ro.observe(wrap);
      if (wrap.previousElementSibling) ro.observe(wrap.previousElementSibling);
    }
    window.addEventListener('resize', measure, { passive: true });
    /* read: where we are and (only when the header is about to hide) whether it is busy; write: the classes */
    const look = (sy) => {
      const y = Math.max(0, sy);
      const goingDown = y > lastY + 4;
      const goingUp = y < lastY - 4;
      const rest = reduce.matches || y < top + hh * 2;
      /* busy() reads layout, so it is only asked when the header is about to hide */
      return { y, goingDown, goingUp, rest, busy: !rest && goingDown && busy() };
    };
    const apply = (s) => {
      root.classList.toggle('ukoo-scrolled', s.y > top + 8);
      if (s.rest) {
        root.classList.remove('ukoo-head-hidden');
      } else if (s.goingDown) {
        root.classList.toggle('ukoo-head-hidden', !s.busy);
      } else if (s.goingUp) {
        root.classList.remove('ukoo-head-hidden');
      }
      if (s.goingDown || s.goingUp) lastY = s.y;
      return false;
    };
    const update = () => {
      ticking = false;
      apply(look(window.scrollY));
    };
    /* on the shared scheduler (its read phase runs before any section writes); the kit is defined further down this
       file, so it is looked up once the whole file has run */
    queueMicrotask(() => {
      const K = window.UKOO && window.UKOO.kit;
      if (K && K.on) K.on(root, (v) => look(v.y), apply);
      else
        window.addEventListener(
          'scroll',
          () => {
            if (!ticking) {
              ticking = true;
              requestAnimationFrame(update);
            }
          },
          { passive: true }
        );
    });
    wrap.addEventListener('focusin', () => root.classList.remove('ukoo-head-hidden'));
    update();
  };

  const init = () => {
    reveal();
    header();
    if (window.UKOO && window.UKOO.sections) window.UKOO.sections();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', () => {
    reveal();
    if (window.UKOO && window.UKOO.sections) window.UKOO.sections();
  });
})();

/* ==========================================================================
   Stage 2 section modules (ported from the gallery mock-ups). Each module binds
   once per element; one rAF loop drives every scroll-linked device.
   ========================================================================== */
(() => {
  const W = window;
  const d = document;
  const html = d.documentElement;
  const rm = W.matchMedia('(prefers-reduced-motion: reduce)');
  const still = () => rm.matches;
  const fine = W.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const seg = (p, a, len) => clamp((p - a) / len, 0, 1);
  const eOut = (t) => 1 - Math.pow(1 - t, 4);
  const eIO = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const lerp = (a, b, t) => a + (b - a) * t;
  const once = (el, k) => {
    if (!el || el.dataset['ukB' + k]) return false;
    el.dataset['ukB' + k] = '1';
    return true;
  };
  /* the shared scheduler (rules: docs/redesign/30-perf-rules.md). Each frame: refresh vp, run every active section's
     read(), then every write(), then the legacy renders. on() sections only run while an IntersectionObserver says
     they are near the viewport, so reads never follow another section's writes and off-screen work costs nothing. */
  const renders = new Set();
  const loops = new Set();
  const vp = { y: 0, h: 0, w: 0 };
  let ticking = false;
  let holdNow = null;
  let holdV = false;
  let lastY = -1e9;
  const frame = () => {
    ticking = false;
    let more = false;
    vp.y = W.scrollY;
    vp.h = W.innerHeight;
    vp.w = W.innerWidth;
    if (holdNow) holdV = holdNow();
    /* a jump (anchor link, Home/End, find in page) moves further in one frame than the observers' margins cover, and
       they only report after the frame is painted: such a frame runs every section once, so none shows a stale pose.
       The threshold equals the smallest margin any section uses (25%: keep every on() margin at 25% or more).
       Ordinary scrolling stays under it (measured max 131 px per frame at 390, in an 8000 px/s fling). */
    const jump = Math.abs(vp.y - lastY) > vp.h * 0.25;
    lastY = vp.y;
    const act = [];
    loops.forEach((L) => (L.near || jump) && act.push(L));
    act.forEach((L) => (L.s = L.read(vp)));
    act.forEach((L) => {
      if (L.write(L.s, vp)) more = true;
    });
    renders.forEach((fn) => {
      if (fn()) more = true;
    });
    if (more) kick();
  };
  const on = (el, read, write, margin) => {
    const L = { el, read, write, near: !('IntersectionObserver' in W), s: null };
    loops.add(L);
    if (!L.near) {
      const m = margin || '25% 0px';
      L.o = new IntersectionObserver(
        (es) => {
          L.near = es[es.length - 1].isIntersecting;
          if (L.near) kick();
        },
        { rootMargin: m }
      );
      L.o.observe(el);
    }
    kick();
    return { off: () => (loops.delete(L), L.o && L.o.disconnect()) };
  };
  const kick = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(frame);
    }
  };
  W.addEventListener('scroll', kick, { passive: true });
  const resizers = new Set();
  let rt;
  W.addEventListener('resize', () => {
    clearTimeout(rt);
    rt = setTimeout(() => {
      resizers.forEach((fn) => fn());
      kick();
    }, 120);
  });
  const io = (els, fn, opts) => {
    if (!('IntersectionObserver' in W)) {
      els.forEach((e) => fn(e));
      return;
    }
    const o = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (e.isIntersecting) {
          fn(e.target);
          o.unobserve(e.target);
        }
      });
    }, opts || { threshold: 0.35 });
    els.forEach((e) => o.observe(e));
  };
  /* phone pass: every phone-only behaviour below checks this; desktop paths are unchanged */
  const phone = W.matchMedia('(max-width: 749px)');
  /* phone: counter + thumb under a horizontal snap row; onPick(k) gets the card nearest the row's centre */
  const snapnav = (row, nav, items, onPick) => {
    if (!row || !nav || !items.length) return () => 0;
    const n = nav.querySelector('[data-n]');
    const t = nav.querySelector('[data-t]');
    const bar = nav.querySelector('.uk-snapnav__bar i');
    let last = -1;
    nav.style.setProperty('--n', items.length);
    if (t) t.textContent = ' / ' + items.length;
    const upd = () => {
      if (!phone.matches) return 0;
      const rr = row.getBoundingClientRect();
      const mid = rr.left + rr.width / 2;
      let k = 0;
      let best = 1e9;
      items.forEach((it, i) => {
        const r = it.getBoundingClientRect();
        const dd = Math.abs(r.left + r.width / 2 - mid);
        if (dd < best) {
          best = dd;
          k = i;
        }
      });
      const max = row.scrollWidth - row.clientWidth;
      if (row.scrollLeft < 4) k = 0;
      if (max > 0 && row.scrollLeft > max - 4) k = items.length - 1;
      if (k !== last) {
        last = k;
        if (n) n.textContent = k + 1;
        if (bar) bar.style.transform = `translateX(${k * 100}%)`;
        nav.classList.toggle('is-end', k === items.length - 1);
        if (onPick) onPick(k);
      }
      return k;
    };
    let rq = 0;
    row.addEventListener(
      'scroll',
      () => {
        if (!rq) rq = requestAnimationFrame(() => ((rq = 0), upd()));
      },
      { passive: true }
    );
    /* a pager with buttons (home peak): previous / next card, disabled at the ends */
    const prev = nav.querySelector('[data-prev]');
    const next = nav.querySelector('[data-next]');
    if (prev && next) {
      const to = (dk) => {
        const it = items[clamp(last + dk, 0, items.length - 1)];
        row.scrollTo({ left: it.offsetLeft + it.offsetWidth / 2 - row.clientWidth / 2, behavior: still() ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', () => to(-1));
      next.addEventListener('click', () => to(1));
      const ends = () => {
        const k = upd();
        prev.disabled = k <= 0;
        next.disabled = k >= items.length - 1;
      };
      row.addEventListener('scroll', () => requestAnimationFrame(ends), { passive: true });
      resizers.add(ends);
      ends();
    }
    resizers.add(upd);
    upd();
    if (!still()) io([row], () => phone.matches && row.scrollLeft < 4 && row.classList.add('is-nudge'), { threshold: 0.6 });
    return upd;
  };

  /* ---------- hero carousel ---------- */
  const hero = (root) => {
    const slides = [...root.querySelectorAll('.uk-slide')];
    if (slides.length < 2) return;
    const bar = root.querySelector('.uk-track i');
    const live = root.querySelector('[data-live]');
    const pp = root.querySelector('.uk-pp');
    const ms = parseFloat(root.dataset.interval) || 7200;
    root.style.setProperty('--dur', ms / 1000 + 's');
    let cur = 0;
    let timer = null;
    let paused = still();
    /* later slides arrive without src (data-src / data-srcset) so they stay out of the first paint's bandwidth */
    const eager = (s) => {
      const im = s && s.querySelector('img');
      if (!im) return;
      /* a phone crop (<picture><source>) goes first, so the phone never fetches the desktop file */
      s.querySelectorAll('source[data-srcset]').forEach((so) => {
        so.srcset = so.dataset.srcset;
        so.removeAttribute('data-srcset');
      });
      if (im.dataset.srcset) im.srcset = im.dataset.srcset;
      if (im.dataset.src) im.src = im.dataset.src;
      im.removeAttribute('data-srcset');
      im.removeAttribute('data-src');
      if (im.loading === 'lazy') im.loading = 'eager';
    };
    /* phone: one story-style segment per slide (hidden at 750 and up) */
    const segs = [...root.querySelectorAll('.uk-hero__segs i')];
    const segsUp = () =>
      segs.forEach((g, i) => {
        g.classList.toggle('is-done', i < cur);
        g.classList.toggle('is-cur', i === cur);
        g.classList.remove('is-run');
        if (i === cur && !paused) {
          void g.offsetWidth;
          g.classList.add('is-run');
        }
      });
    const runBar = () => {
      bar.classList.remove('is-run');
      void bar.offsetWidth;
      if (!paused) bar.classList.add('is-run');
      segsUp();
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!paused) timer = setTimeout(() => go(cur + 1), ms);
    };
    const go = (n) => {
      const old = slides[cur];
      old.classList.remove('is-on');
      old.setAttribute('aria-hidden', 'true');
      old.querySelectorAll('a').forEach((a) => (a.tabIndex = -1));
      cur = (n + slides.length) % slides.length;
      const s = slides[cur];
      eager(s);
      s.classList.add('is-on');
      s.removeAttribute('aria-hidden');
      s.querySelectorAll('a').forEach((a) => a.removeAttribute('tabindex'));
      eager(slides[(cur + 1) % slides.length]);
      if (live) live.textContent = cur + 1 + ' / ' + slides.length;
      runBar();
      schedule();
    };
    const setPaused = (p) => {
      paused = p;
      root.classList.toggle('is-paused', p);
      pp.setAttribute('aria-label', p ? 'Play slideshow' : 'Pause slideshow');
      if (p) {
        clearTimeout(timer);
        segsUp();
      } else {
        runBar();
        schedule();
      }
    };
    pp.addEventListener('click', () => setPaused(!paused));
    root.querySelector('.uk-hb--prev').addEventListener('click', () => go(cur - 1));
    root.querySelector('.uk-hb--next').addEventListener('click', () => go(cur + 1));
    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(cur + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(cur - 1);
      }
    });
    d.addEventListener('visibilitychange', () => (d.hidden ? clearTimeout(timer) : schedule()));
    /* phone: swipe between slides */
    let sx = null;
    let sy = 0;
    root.addEventListener(
      'touchstart',
      (e) => {
        if (!phone.matches || e.touches.length !== 1) return;
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      },
      { passive: true }
    );
    root.addEventListener(
      'touchend',
      (e) => {
        if (sx == null) return;
        const dx = e.changedTouches[0].clientX - sx;
        const dy = e.changedTouches[0].clientY - sy;
        sx = null;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.4) go(cur + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );
    const start = parseInt(new URLSearchParams(location.search).get('slide'), 10);
    if (start >= 1 && start <= slides.length) {
      paused = true;
      go(start - 1);
    }
    setPaused(paused);
    const all = () => slides.forEach(eager);
    if (d.readyState === 'complete') all();
    else W.addEventListener('load', all, { once: true });
  };

  /* ---------- range peak: the line-up walks into the shop ---------- */
  const peak = (root) => {
    const stage = root.querySelector('[data-stage]');
    const ph = root.querySelector('[data-h]');
    const ph2 = root.querySelector('[data-h2f]');
    const pbs = [...root.querySelectorAll('.uk-pb')];
    const cards = [...root.querySelectorAll('.uk-card')];
    const infos = cards.map((c) => c.querySelector('.uk-card__info'));
    const aside = root.querySelector('[data-aside]');
    const floor = root.querySelector('.uk-peak__floor');
    const row = root.querySelector('[data-row]');
    const hblock = root.querySelector('.uk-peak__hero');
    /* pinned from 700 px tall (was 620): below that the landed row of three, with its purchase choice, would leave the
       card photos too short, so short windows get the composed flow layout instead (Ryan 2026-10-14) */
    const pinMQ = W.matchMedia('(min-width: 821px) and (min-height: 700px)');
    const r2 = root.parentNode && root.parentNode.querySelector('.uk-peak--r2');
    const r2row = r2 && r2.querySelector('.uk-peak__row');
    let pinOn = false;
    let M = null;
    const ptr = { x: 0, y: 0, tx: 0, ty: 0 };
    const REST = [
      { x: 0, y: -14, s: 0.92 },
      { x: 0, y: 0, s: 1 },
      { x: 0, y: -14, s: 0.92 },
    ];
    const FAN = [
      { x: -40, y: -40, s: 0.88, r: -3.5 },
      { x: 0, y: -58, s: 0.92, r: 0 },
      { x: 22, y: -34, s: 0.86, r: 3.5 },
    ];
    const RISE_AT = [0.035, 0, 0.06];
    const eS = (t) => 0.5 - 0.5 * Math.cos(Math.PI * t);
    const SETTLE_AT = [0.025, 0, 0.025];
    /* the cut-out's own bottle inside its image: top gap, height, bottom gap (fractions; the cut-outs are cropped tight) */
    const CUT = [0.0025, 0.995, 0.0025];
    /* where bottle i lands, as its bottle's bottom centre and height: on the landing photo's own bottle when the card shows
       one (data-bb = the bottle's box in the photo, as fractions "x,y,w,h", mapped through object-fit: cover and the
       photo's object-position), else on the card's cut-out slot */
    const landing = (i) => {
      const c = cards.find((x) => x.dataset.b === String(i));
      if (!c) return null;
      const media = c.querySelector('.uk-card__media');
      const im = media && media.querySelector('.uk-card__ph');
      const bb = media && (media.dataset.bb || '').split(',').map(Number);
      if (im && bb && bb.length === 4 && bb.every((v) => v >= 0) && getComputedStyle(im).display !== 'none') {
        /* the photo's own box, from layout (never a hover scale): the card photo box on desktop, the larger zoomed
           box on phones (zoomM) */
        const mr = media.getBoundingClientRect();
        const cs = getComputedStyle(im);
        const m = { left: mr.left + (parseFloat(cs.left) || 0), top: mr.top + (parseFloat(cs.top) || 0), width: parseFloat(cs.width) || mr.width, height: parseFloat(cs.height) || mr.height };
        const iw = +im.getAttribute('width') || im.naturalWidth || 1;
        const ih = +im.getAttribute('height') || im.naturalHeight || 1;
        const k = Math.max(m.width / iw, m.height / ih);
        const dw = iw * k;
        const dh = ih * k;
        const pos = getComputedStyle(im).objectPosition.split(' ');
        const f = (v, free) => (v.endsWith('%') ? (parseFloat(v) / 100) * free : parseFloat(v) || 0);
        const x0 = m.left + f(pos[0] || '50%', m.width - dw);
        const y0 = m.top + f(pos[1] || '50%', m.height - dh);
        return { cx: x0 + (bb[0] + bb[2] / 2) * dw, bottom: y0 + (bb[1] + bb[3]) * dh, h: bb[3] * dh };
      }
      const slot = c.querySelector('.uk-card__cb');
      if (!slot) return null;
      const t = slot.getBoundingClientRect();
      return { cx: t.left + t.width / 2, bottom: t.bottom - CUT[2] * t.height, h: CUT[1] * t.height };
    };
    /* once the bottles have landed (is-landed), each walking bottle hands over to its card's product photo with a short
       timed fade (CSS): the bottle goes first, then the photo, so a paused scroll never shows both half-visible.
       Static layouts show the photo. */
    pbs.forEach((pb) => {
      const c = cards.find((c) => c.dataset.b === pb.dataset.i);
      if (c && c.querySelector('.uk-card__ph')) pb.classList.add('has-ph');
    });
    /* the walking bottles and the landed photos load with the page (low priority), not lazily mid-scroll (round 2) */
    if (!still()) root.querySelectorAll('.uk-pb img, .uk-card__ph').forEach((im) => { im.fetchPriority = 'low'; im.loading = 'eager'; });
    const clearT = () => {
      pbs.concat([ph, aside, floor], cards, infos).forEach((e) => {
        if (!e) return;
        e.style.transform = '';
        e.style.opacity = '';
      });
    };
    /* pinned, three per row (Ryan 2026-10-14): the landed row hangs from just under the header, or sits on the stage
       floor when it fits. Its photos are tall enough to show the whole bottle each walking bottle lands on (each photo
       is framed on its bottle); whatever of the card text and purchase choice does not fit runs below the stage and
       comes into view as the pin releases, so row 2 (after the section) starts that much lower (--ov). Both rows share
       the photo height. */
    const phPos = new Map();
    const rowPh = () => cards.map((c) => c.querySelector('.uk-card__media--ph')).filter((m) => m && m.offsetWidth > 0);
    const sizeRow = () => {
      unsizeRow();
      const meds = rowPh();
      if (!meds.length) return;
      const w = meds[0].offsetWidth;
      const hd = d.querySelector('.section-site-header');
      const top = (hd ? hd.offsetHeight : 0) + 24;
      const sr = stage.getBoundingClientRect();
      const link = row.querySelector('.uk-peak__all');
      const lh = link ? link.getBoundingClientRect().height + (parseFloat(getComputedStyle(row).rowGap) || 0) + (parseFloat(getComputedStyle(link).marginBottom) || 0) : 0;
      /* tall enough for the tallest studio bottle with 6% air above and below (at least 0.8 of the card width); never so
         tall that the titles leave the screen */
      const need = Math.max(
        w * 0.8,
        ...meds.map((m) => {
          const im = m.querySelector('.uk-card__ph');
          const bb = (m.dataset.bb || '').split(',').map(Number);
          return im && bb.length === 4 ? (bb[3] * w * (+im.getAttribute('height') || 1)) / (+im.getAttribute('width') || 1) / 0.88 : 0;
        })
      );
      const mh = Math.round(clamp(need, 150, sr.height - top - lh - 84));
      root.style.setProperty('--rt', `${top}px`);
      root.style.setProperty('--mh', `${mh}px`);
      if (r2) r2.style.setProperty('--mh', `${mh}px`);
      meds.forEach((m) => {
        const im = m.querySelector('.uk-card__ph');
        const bb = (m.dataset.bb || '').split(',').map(Number);
        if (!im || bb.length !== 4) return;
        const iw = +im.getAttribute('width') || 1;
        const ih = +im.getAttribute('height') || 1;
        const k = Math.max(w / iw, mh / ih);
        const dh = ih * k;
        if (dh - mh < 1) return;
        const py = clamp(((bb[1] + bb[3] / 2) * dh - mh / 2) / (dh - mh), 0, 1);
        if (!phPos.has(im)) phPos.set(im, im.style.objectPosition);
        im.style.objectPosition = `${getComputedStyle(im).objectPosition.split(' ')[0]} ${(py * 100).toFixed(2)}%`;
      });
      /* a row that fits sits on the stage floor (3% up, as before), so no empty band opens above row 2 on tall screens */
      const fitTop = sr.height * 0.97 - row.getBoundingClientRect().height;
      if (fitTop > top) root.style.setProperty('--rt', `${Math.round(fitTop)}px`);
      /* + 50: the delivery select (44 px + gap) that opens under a card when The Refill Ritual is chosen */
      const ov = Math.max(0, Math.ceil(row.getBoundingClientRect().bottom - sr.bottom + sr.height * 0.03 + 50));
      if (r2) r2.style.setProperty('--ov', `${ov}px`);
    };
    const unsizeRow = () => {
      ['--mh', '--rt'].forEach((v) => root.style.removeProperty(v));
      if (r2) ['--mh', '--ov'].forEach((v) => r2.style.removeProperty(v));
      rowPh().forEach((m) => {
        const im = m.querySelector('.uk-card__ph');
        if (im && phPos.has(im)) im.style.objectPosition = phPos.get(im);
      });
    };
    const measure = () => {
      clearT();
      const sr = stage.getBoundingClientRect();
      const hr = ph.getBoundingClientRect();
      const fr = ph2.getBoundingClientRect();
      M = {
        top: root.getBoundingClientRect().top + W.scrollY,
        travel: Math.max(root.offsetHeight - W.innerHeight, 1),
        h: { dx: fr.left - hr.left, dy: fr.top - hr.top, s: parseFloat(getComputedStyle(ph2).fontSize) / parseFloat(getComputedStyle(ph).fontSize) },
        b: pbs.map((pb) => {
          const r = pb.getBoundingClientRect();
          /* the final pose is solved on the untransformed box (the CSS rest pose scales the side bottles), about the
             bottle's transform origin (bottom centre), so the walking bottle ends exactly on its target bottle */
          const hb = pb.offsetParent.getBoundingClientRect();
          const w0 = pb.offsetWidth;
          const h0 = pb.offsetHeight;
          const ox = hb.left + pb.offsetLeft + w0 / 2;
          const oy = hb.top + pb.offsetTop + h0;
          const tg = landing(+pb.dataset.i);
          if (!tg) return { ex: 0, ey: 0, es: 1, below: r.height * 1.08 + (sr.bottom - r.bottom) + 40 };
          const es = tg.h / (CUT[1] * h0);
          return { ex: tg.cx - ox, ey: tg.bottom - oy + es * CUT[2] * h0, es, below: r.height * 1.08 + (sr.bottom - r.bottom) + 40 };
        }),
      };
    };
    const renderPin = (sy = W.scrollY) => {
      if (!M) return false;
      const p = clamp((sy - M.top) / M.travel, -1, 1);
      ptr.x += (ptr.tx - ptr.x) * 0.08;
      ptr.y += (ptr.ty - ptr.y) * 0.08;
      const th = eIO(seg(p, 0.34, 0.36));
      const drift = -20 * seg(p, 0, 0.34);
      const hx = lerp(0, M.h.dx, th) - ptr.x * 4 * (1 - th);
      const hy = lerp(drift, M.h.dy, th) - ptr.y * 3 * (1 - th);
      const hs = lerp(1, M.h.s, th);
      ph.style.transform = `translate3d(${hx.toFixed(2)}px,${hy.toFixed(2)}px,0) scale(${hs.toFixed(4)})`;
      /* the headline is gone before the cards arrive (p 0.6) and long before the landing (0.82): no text over a card */
      ph.style.opacity = (1 - eIO(seg(p, 0.4, 0.2))).toFixed(3);
      pbs.forEach((pb, k) => {
        const i = +pb.dataset.i;
        const b = M.b[k];
        const R = REST[i];
        const F = FAN[i];
        /* the rise eases in and out over the first 30% of the pin (a sine ease, gentlest peak speed), so the bottles
           never leap (round 2) */
        const t1 = eS(seg(p, RISE_AT[i], 0.3));
        const t2 = eIO(seg(p, 0.3, 0.18));
        const t3 = eS(seg(p, 0.5 + SETTLE_AT[i], 0.28));
        let x = R.x;
        let y = lerp(b.below, R.y, t1);
        let s = R.s;
        let r = 0;
        x = lerp(x, F.x, t2);
        y = lerp(y, F.y, t2);
        s = lerp(s, F.s, t2);
        r = lerp(r, F.r, t2);
        x = lerp(x, b.ex, t3);
        y = lerp(y, b.ey, t3);
        s = lerp(s, b.es, t3);
        r = lerp(r, 0, t3);
        const kk = (1 - t3) * (i === 1 ? 1 : 0.75);
        x += ptr.x * 10 * kk;
        y += ptr.y * 6 * kk;
        pb.style.transform = `translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,0) rotate(${r.toFixed(3)}deg) scale(${s.toFixed(4)})`;
        const sh = pb.querySelectorAll('i');
        sh[0].style.opacity = (t1 * t1 * (1 - t3)).toFixed(3);
        sh[1].style.opacity = (t1 * t1).toFixed(3);
      });
      root.classList.toggle('is-landed', p > 0.82);
      cards.forEach((c, ci) => {
        let a;
        let tf;
        if (c.dataset.b != null) {
          const bi = +c.dataset.b;
          a = eOut(seg(p, 0.6 + (bi === 1 ? 0 : 0.04), 0.16));
          tf = `translate3d(0,${((1 - a) * 30).toFixed(1)}px,0)`;
        } else {
          a = eOut(seg(p, 0.6, 0.18));
          tf = `translate3d(${((1 - a) * (ci === 0 ? -90 : 90)).toFixed(1)}px,0,0)`;
        }
        c.style.opacity = a.toFixed(3);
        c.style.transform = tf;
        const ti = eOut(seg(p, 0.74 + Math.abs(ci - 2) * 0.02, 0.12));
        infos[ci].style.opacity = ti.toFixed(3);
        infos[ci].style.transform = `translate3d(0,${((1 - ti) * 14).toFixed(1)}px,0)`;
      });
      const ta = eOut(seg(p, 0.62, 0.18));
      aside.style.opacity = ta.toFixed(3);
      aside.style.transform = `translate3d(0,${((1 - ta) * 16).toFixed(1)}px,0)`;
      floor.style.opacity = (1 - eIO(seg(p, 0.5, 0.25))).toFixed(3);
      row.classList.toggle('is-live', p > 0.84);
      return Math.abs(ptr.tx - ptr.x) > 0.002 || Math.abs(ptr.ty - ptr.y) > 0.002;
    };
    const renderFlow = (r = hblock.getBoundingClientRect(), vh = W.innerHeight) => {
      if (r.bottom < -50 || r.top > vh + 50) return false;
      const t = eOut(clamp((vh - r.top) / (vh * 0.8), 0, 1));
      const u = eIO(clamp((vh * 0.45 - r.top) / (vh * 0.6), 0, 1));
      pbs.forEach((pb) => {
        const i = +pb.dataset.i;
        const R = REST[i];
        const side = i === 0 ? -1 : i === 2 ? 1 : 0;
        const y = lerp(r.height * 0.42, R.y, t) - u * 10 * (side ? 1 : 1.4);
        const x = side * u * 14;
        const rot = side * u * 2.5;
        pb.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0) rotate(${rot.toFixed(2)}deg) scale(${R.s})`;
      });
      return false;
    };
    /* phone (is-mpin, max-width 749): the words slide in and the bottles rise as the stage arrives, then a short pin
       (the section is 190svh) fans them, folds the headline into a lockup and walks the bottles into a centred snap
       row; once settled the cards' own cut-outs take over so the row can be swiped. */
    const wds = [...ph.querySelectorAll('.uk-wd')];
    const nav = root.querySelector('[data-snapnav]');
    let mpinOn = false;
    let lastW = 0;
    const FAN_M = [
      { x: -20, y: -24, s: 0.9, r: -4 },
      { x: 0, y: -40, s: 0.95, r: 0 },
      { x: 16, y: -20, s: 0.88, r: 4 },
    ];
    const RISE_M = [0.06, 0, 0.1];
    const clearM = () => {
      wds.concat([nav]).forEach((e) => {
        if (!e) return;
        e.style.transform = '';
        e.style.opacity = '';
      });
      root.classList.remove('is-mset');
      row.classList.remove('is-live');
    };
    const centreRow = () => {
      const c = cards.find((x) => x.dataset.b === '1') || cards[Math.floor(cards.length / 2)];
      if (c) row.scrollLeft = c.offsetLeft + c.offsetWidth / 2 - row.clientWidth / 2;
    };
    /* phones (Ryan 2026-10-14): the landed card shows the product's studio photo zoomed in on its bottle (the bottle
       about 84% of the photo box's height), so the product fills the card; the walking bottle lands on that bottle */
    const zoomed = new Map();
    const unzoomM = () => {
      zoomed.forEach((css, im) => (im.style.cssText = css));
      zoomed.clear();
    };
    /* the photo height comes from the real card (title lines, purchase choice) so the card, its Add (with 50px kept for
       the delivery select) and the pager always fit the stage; where even then a whole studio bottle cannot fit, the
       cards keep their cut-out bottle instead (is-mcut, as before) */
    /* worked out once per screen size and font state (never on a re-measure: a chosen Refill Ritual's delivery select
       would otherwise be counted twice and the photo would shrink while being looked at) */
    let mKey = '';
    const sizeM = () => {
      const key = `${W.innerWidth}x${stage.clientHeight}:${d.fonts ? d.fonts.status : ''}`;
      if (key === mKey) return;
      mKey = key;
      root.style.removeProperty('--mmh');
      root.classList.remove('is-mcut');
      const c = cards.find((x) => x.dataset.b === '1') || cards[0];
      const media = c && c.querySelector('.uk-card__media');
      if (!media || !media.clientWidth) {
        mKey = '';
        return;
      }
      const sr = stage.getBoundingClientRect();
      const rowTop = row.getBoundingClientRect().top - sr.top;
      const navH = nav ? nav.offsetHeight + (parseFloat(getComputedStyle(nav).marginTop) || 0) : 0;
      const rest = c.offsetHeight - media.offsetHeight;
      const w = media.clientWidth;
      const mh = Math.round(clamp(stage.clientHeight - rowTop - navH - rest - 50 - 8, 120, W.innerWidth * 1.04));
      root.style.setProperty('--mmh', `${mh}px`);
      const cut = cards.some((x) => {
        const m = x.querySelector('.uk-card__media--ph');
        const im = m && m.querySelector('.uk-card__ph');
        const bb = m && (m.dataset.bb || '').split(',').map(Number);
        if (!im || !bb || bb.length !== 4) return false;
        const iw = +im.getAttribute('width') || 1;
        const ih = +im.getAttribute('height') || 1;
        /* the zoom zoomM will use: does the whole bottle fit, in height and in width? */
        const k = Math.max(w / iw, mh / ih, (0.84 * mh) / (bb[3] * ih));
        return bb[3] * ih * k > mh * 0.94 || bb[2] * iw * k > w * 0.94;
      });
      root.classList.toggle('is-mcut', cut);
    };
    const zoomM = () => {
      unzoomM();
      if (root.classList.contains('is-mcut')) return;
      cards.forEach((c) => {
        const media = c.querySelector('.uk-card__media--ph');
        const im = media && media.querySelector('.uk-card__ph');
        const bb = media && (media.dataset.bb || '').split(',').map(Number);
        if (!im || !bb || bb.length !== 4) return;
        const w = media.clientWidth;
        const h = media.clientHeight;
        const iw = +im.getAttribute('width') || 1;
        const ih = +im.getAttribute('height') || 1;
        if (!w || !h) return;
        const k = Math.max(w / iw, h / ih, (0.84 * h) / (bb[3] * ih));
        const dw = iw * k;
        const dh = ih * k;
        const x = clamp(w / 2 - (bb[0] + bb[2] / 2) * dw, w - dw, 0);
        const y = clamp(h / 2 - (bb[1] + bb[3] / 2) * dh, h - dh, 0);
        zoomed.set(im, im.style.cssText);
        im.style.cssText += `;inset:auto;left:${x.toFixed(1)}px;top:${y.toFixed(1)}px;width:${dw.toFixed(1)}px;height:${dh.toFixed(1)}px;max-width:none;object-fit:fill`;
      });
    };
    const measureM = () => {
      unzoomM();
      sizeM();
      zoomM();
      measure();
      const sr = stage.getBoundingClientRect();
      const hr = ph.getBoundingClientRect();
      const rr = row.getBoundingClientRect();
      const top = Math.max(64, W.innerHeight * 0.075);
      /* the folded headline ends above "Shop the full range" when the row carries it, else above the row */
      const lk = root.querySelector('.uk-peak__all--m');
      const end = lk && lk.getClientRects().length ? lk.getBoundingClientRect().top - 4 : rr.top - 22;
      M.h = { dx: 0, dy: top - (hr.top - sr.top), s: clamp((end - sr.top - top) / hr.height, 0.28, 0.42) };
      M.vh = W.innerHeight;
      M.sl = row.scrollLeft;
    };
    const renderM = (sy = W.scrollY, sl = row.scrollLeft) => {
      if (!M) return false;
      const y = sy - M.top;
      const q = y / M.vh;
      const p = clamp(y / M.travel, 0, 1);
      const set = p > 0.86;
      /* only after the row was swiped and the page scrolls back up: re-measuring has to clear the poses first, so this one
         path reads layout in the write phase (rare, and as before) */
      if (!set && Math.abs(sl - M.sl) > 1) measureM();
      wds.forEach((w, k) => {
        const side = w.classList.contains('uk-wd--r') ? 1 : -1;
        const tw = eOut(seg(q, -0.85 + k * 0.06, 0.6));
        /* a short slide (less than the gutter), so no word ever starts off-screen (round 4) */
        w.style.transform = `translate3d(${((1 - tw) * side * 14).toFixed(1)}px,0,0)`;
        w.style.opacity = tw.toFixed(3);
      });
      const t2h = eIO(seg(p, 0, 0.3));
      const th = eIO(seg(p, 0.2, 0.45));
      ph.style.transform = `translate3d(0,${lerp(-14 * t2h, M.h.dy, th).toFixed(2)}px,0) scale(${lerp(1, M.h.s, th).toFixed(4)})`;
      /* the headline is gone before the cards arrive (p 0.5): the landed screen is the row alone (Ryan 2026-10-14) */
      ph.style.opacity = (1 - eIO(seg(p, 0.22, 0.26))).toFixed(3);
      pbs.forEach((pb, k) => {
        const i = +pb.dataset.i;
        const b = M.b[k];
        const R = REST[i];
        const F = FAN_M[i];
        /* the rise runs on into the pin, overlapping the fan, so there is no stall between them (round 2) */
        const t1 = eIO(seg(q, -0.6 + RISE_M[i], 0.75));
        const t2 = eIO(seg(p, 0, 0.3));
        const t3 = eIO(seg(p, 0.12 + SETTLE_AT[i] * 1.6, 0.58));
        const x = lerp(lerp(R.x, F.x, t2), b.ex, t3);
        const yy = lerp(lerp(lerp(b.below, R.y, t1), F.y, t2), b.ey, t3);
        const s = lerp(lerp(R.s, F.s, t2), b.es, t3);
        const r = lerp(F.r * t2, 0, t3);
        pb.style.transform = `translate3d(${x.toFixed(2)}px,${yy.toFixed(2)}px,0) rotate(${r.toFixed(3)}deg) scale(${s.toFixed(4)})`;
        const sh = pb.querySelectorAll('i');
        sh[0].style.opacity = (t1 * t1 * (1 - t3)).toFixed(3);
        sh[1].style.opacity = (t1 * t1).toFixed(3);
      });
      root.classList.toggle('is-landed', p > 0.76);
      cards.forEach((c, ci) => {
        const bt = c.dataset.b != null;
        const a = eOut(seg(p, bt ? 0.5 + (c.dataset.b === '1' ? 0 : 0.05) : 0.56, 0.26));
        c.style.opacity = a.toFixed(3);
        c.style.transform = bt ? `translate3d(0,${((1 - a) * 26).toFixed(1)}px,0)` : `translate3d(${((1 - a) * (ci < 2 ? -60 : 60)).toFixed(1)}px,0,0)`;
        const ti = eOut(seg(p, 0.64 + Math.abs(ci - 2) * 0.03, 0.2));
        infos[ci].style.opacity = ti.toFixed(3);
        infos[ci].style.transform = `translate3d(0,${((1 - ti) * 12).toFixed(1)}px,0)`;
      });
      floor.style.opacity = (1 - eIO(seg(p, 0.3, 0.3))).toFixed(3);
      const tn = eOut(seg(p, 0.66, 0.22));
      [nav, aside].forEach((e) => {
        if (!e) return;
        e.style.opacity = tn.toFixed(3);
        e.style.transform = `translate3d(0,${((1 - tn) * 10).toFixed(1)}px,0)`;
      });
      root.classList.toggle('is-mset', set);
      row.classList.toggle('is-live', set);
      return false;
    };
    snapnav(row, nav, cards, (k) => cards.forEach((c, i) => c.classList.toggle('is-c', i === k)));
    const setup = () => {
      const wantM = phone.matches && !still() && pbs.length > 0;
      if (wantM !== mpinOn) {
        mpinOn = wantM;
        root.classList.toggle('is-mpin', mpinOn);
        if (!mpinOn) clearM();
      }
      if (mpinOn) {
        if (pinOn) {
          pinOn = false;
          root.classList.remove('is-pin');
          if (r2) r2.classList.remove('is-sized');
        }
        unsizeRow();
        clearT();
        M = null;
        if (W.innerWidth !== lastW) {
          lastW = W.innerWidth;
          centreRow();
        }
        measureM();
        renderM();
        return;
      }
      unzoomM();
      root.style.removeProperty('--mmh');
      root.classList.remove('is-mcut');
      mKey = '';
      const want = pinMQ.matches && !still() && pbs.length > 0;
      if (want !== pinOn) {
        pinOn = want;
        root.classList.toggle('is-pin', pinOn);
        if (r2) r2.classList.toggle('is-sized', pinOn);
      }
      clearT();
      M = null;
      if (pinOn) {
        sizeRow();
        measure();
        renderPin();
      } else {
        unsizeRow();
        if (!still()) renderFlow();
      }
    };
    /* the Judge.me popup also stays away while a product row is on screen (Ryan 2026-10-14, option A), so it never
       covers a card's Add; the observer keeps this free of per-frame reads */
    let rowsOn = 0;
    if ('IntersectionObserver' in W) {
      const on = new Set();
      const ob = new IntersectionObserver((es) => {
        es.forEach((e) => (e.isIntersecting ? on.add(e.target) : on.delete(e.target)));
        rowsOn = on.size;
        kick();
      });
      [row, r2row].forEach((e) => e && ob.observe(e));
    }
    root.__ukHold = () => rowsOn > 0 || ((pinOn || mpinOn) && M && W.scrollY > M.top - W.innerHeight && W.scrollY < M.top + M.travel);
    row.addEventListener('focusin', () => {
      if ((pinOn || mpinOn) && M && W.scrollY < M.top + M.travel * 0.9) W.scrollTo(0, M.top + M.travel);
    });
    cards.forEach((c) => {
      if (c.dataset.b == null) return;
      const pb = pbs.find((x) => x.dataset.i === c.dataset.b);
      if (!pb) return;
      c.addEventListener('mouseenter', () => pinOn && pb.classList.add('is-hov'));
      c.addEventListener('mouseleave', () => pb.classList.remove('is-hov'));
    });
    if (fine) {
      stage.addEventListener('pointermove', (e) => {
        ptr.tx = (e.clientX / W.innerWidth - 0.5) * 2;
        ptr.ty = (e.clientY / W.innerHeight - 0.5) * 2;
        kick();
      });
      stage.addEventListener('pointerleave', () => {
        ptr.tx = 0;
        ptr.ty = 0;
        kick();
      });
    }
    /* scheduler: read() takes the row's scroll (phone) or the headline block's box (flow); write() only sets styles.
       A wide margin keeps the stage settled ahead of a fast scroll; a jump runs it at once (see frame()). */
    const flowOn = () => !mpinOn && !pinOn && !still() && !pinMQ.matches;
    /* the pinned stages follow a smoothed scroll position (frame-rate independent ease toward scrollY), so wheel notches
       and uneven touch input become one continuous glide (round 2, "smooth like butter"). A frame that moves more than
       the scheduler's jump threshold (0.25 of a screen: anchor, Home/End) snaps instead. */
    let ys = null;
    let yr = null;
    let yt = 0;
    const glide = (sy, vh) => {
      const now = performance.now();
      const dt = yt ? Math.min(64, now - yt) : 16.7;
      yt = now;
      const jump = yr !== null && Math.abs(sy - yr) > vh * 0.25;
      yr = sy;
      if (ys === null || jump) ys = sy;
      else ys += (sy - ys) * (1 - Math.pow(0.86, dt / 16.7));
      /* a fast flick never leaves the stage more than 0.15 of a screen behind (the phone pin is only ~0.9 screens) */
      ys = Math.min(sy + vh * 0.15, Math.max(sy - vh * 0.15, ys));
      if (Math.abs(sy - ys) < 0.25) ys = sy;
      return ys;
    };
    on(root, () => (mpinOn ? row.scrollLeft : flowOn() ? hblock.getBoundingClientRect() : null), (s, v) => {
      if (!mpinOn && !pinOn) {
        ys = null;
        yr = null;
        return flowOn() && s ? renderFlow(s, v.h) : false;
      }
      const y = glide(v.y, v.h);
      const more = mpinOn ? renderM(y, s) : renderPin(y);
      if (y === v.y) yt = 0;
      return more || y !== v.y;
    }, '100% 0px');
    resizers.add(setup);
    /* turning reduced motion on or off mid-visit re-runs the layout choice, so no pinned pose or zoom is left behind */
    if (rm.addEventListener) rm.addEventListener('change', setup);
    setup();
    if (d.fonts && d.fonts.ready) d.fonts.ready.then(() => { setup(); kick(); });
    W.addEventListener('load', () => {
      setup();
      kick();
      /* the walking bottles and the landed photos are decoded ahead, so neither waits on a decode mid-scroll (the
         photos sit at opacity 0 until the hand-over) */
      if (pinOn || mpinOn) root.querySelectorAll('.uk-pb img, .uk-card__ph').forEach((im) => im.decode && im.decode().catch(() => {}));
    });
  };

  /* ---------- add to bag (cards): real cart, Shapes drawer ---------- */
  const addButtons = (root) => {
    root.querySelectorAll('[data-uk-add]').forEach((b) => {
      if (!once(b, 'add')) return;
      b.addEventListener('click', async () => {
        if (b.getAttribute('aria-busy') === 'true') return;
        b.setAttribute('aria-busy', 'true');
        const t = b.querySelector('span');
        const label = t.textContent;
        try {
          const res = await fetch((W.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: +b.dataset.ukAdd, quantity: 1 }] }),
          });
          if (!res.ok) throw new Error('add failed');
          b.classList.add('is-done');
          t.textContent = 'Added';
          d.dispatchEvent(new CustomEvent('theme:update:cart'));
          setTimeout(() => d.dispatchEvent(new CustomEvent('theme:open:cart-drawer')), 350);
          setTimeout(() => {
            b.classList.remove('is-done');
            t.textContent = label;
          }, 1800);
        } catch (e) {
          t.textContent = label;
        } finally {
          b.removeAttribute('aria-busy');
        }
      });
    });
  };

  /* ---------- reviews: ink-in quotes, tabs ---------- */
  const quotes = (root) => {
    const qs = [...root.querySelectorAll('.uk-quote')];
    const tabs = [...root.querySelectorAll('.uk-tab')];
    if (!qs.length) return;
    let qi = 0;
    let qt = null;
    let seen = still();
    qs.forEach((q) => {
      const p = q.querySelector('p');
      const words = p.textContent.split(' ');
      p.textContent = '';
      words.forEach((w, i) => {
        const s = d.createElement('span');
        s.className = 'uk-w';
        s.style.setProperty('--w', i);
        s.textContent = w;
        p.appendChild(s);
        if (i < words.length - 1) p.appendChild(d.createTextNode(' '));
      });
      if (still()) q.classList.add('is-inked');
    });
    const ink = (q) => seen && q.classList.add('is-inked');
    const show = (n) => {
      qs[qi].classList.remove('is-on');
      if (tabs[qi]) tabs[qi].setAttribute('aria-selected', 'false');
      qi = n % qs.length;
      qs[qi].classList.add('is-on');
      if (tabs[qi]) tabs[qi].setAttribute('aria-selected', 'true');
      ink(qs[qi]);
    };
    const sched = () => {
      clearInterval(qt);
      if (!still() && seen && qs.length > 1) qt = setInterval(() => show(qi + 1), 7600);
    };
    tabs.forEach((t, i) =>
      t.addEventListener('click', () => {
        seen = true;
        show(i);
        sched();
      })
    );
    io(
      [root.querySelector('.uk-quotes')],
      () => {
        seen = true;
        ink(qs[qi]);
        sched();
      },
      { threshold: 0.4 }
    );
    /* phone: swipe between quotes */
    const qbox = root.querySelector('.uk-quotes');
    let qx = null;
    let qy = 0;
    qbox.addEventListener(
      'touchstart',
      (e) => {
        if (!phone.matches || qs.length < 2 || e.touches.length !== 1) return;
        qx = e.touches[0].clientX;
        qy = e.touches[0].clientY;
      },
      { passive: true }
    );
    qbox.addEventListener(
      'touchend',
      (e) => {
        if (qx == null) return;
        const dx = e.changedTouches[0].clientX - qx;
        const dy = e.changedTouches[0].clientY - qy;
        qx = null;
        if (Math.abs(dx) < 44 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
        seen = true;
        show((qi + (dx < 0 ? 1 : qs.length - 1)) % qs.length);
        sched();
      },
      { passive: true }
    );
  };

  /* ---------- feature: story wipe, close scale, split wipe ---------- */
  const feature = (root) => {
    const kind = root.dataset.ukFeat;
    if (kind === 'close') io([root], () => root.classList.add('is-in'));
    if (still()) {
      root.classList.add('is-in');
      return;
    }
    /* scheduler: read() measures, write() only sets styles; the scale variables go on the image that uses them (not
       the section), so a frame restyles one element instead of the whole section */
    const rect = (el) => () => el.getBoundingClientRect();
    if (kind === 'story') {
      const fig = root.querySelector('[data-wipe]');
      const ims = [...fig.querySelectorAll('img')];
      on(fig, rect(fig), (r, v) => {
        const vh = v.h;
        if (r.bottom < 0 || r.top > vh) return false;
        const t = clamp((vh - r.top) / (r.height * 0.48 + vh * 0.48), 0, 1);
        fig.style.setProperty('--wipe', ((1 - t) * 100).toFixed(2) + '%');
        const ws = (1.16 - 0.16 * clamp((vh - r.top) / (vh * 0.6 + r.height * 0.6), 0, 1)).toFixed(4);
        ims.forEach((im) => im.style.setProperty('--ws', ws));
        return false;
      }, '25% 0px');
    } else if (kind === 'close') {
      const bgs = [...root.querySelectorAll('.uk-feat__bg')];
      on(root, rect(root), (r, v) => {
        const vh = v.h;
        if (r.bottom < 0 || r.top > vh) return false;
        const cs = (1.14 - 0.14 * clamp((vh - r.top) / (vh * 0.7 + r.height * 0.7), 0, 1)).toFixed(4);
        bgs.forEach((b) => b.style.setProperty('--cs', cs));
        return false;
      }, '25% 0px');
    } else if (kind === 'split') {
      const fig = root.querySelector('[data-cwipe]');
      on(fig, rect(fig), (r, v) => {
        const vh = v.h;
        if (r.bottom < 0 || r.top > vh) return false;
        const q = clamp((vh - r.top) / (vh * 0.75), 0, 1);
        fig.style.setProperty('--cw', ((1 - eIO(q)) * 100).toFixed(2) + '%');
        return false;
      }, '25% 0px');
    }
  };

  /* ---------- ingredients: active group drives the pinned image ---------- */
  const ingredients = (root) => {
    const grps = [...root.querySelectorAll('.uk-grp')];
    const imgs = [...root.querySelectorAll('[data-stack] img')];
    const bars = [...root.querySelectorAll('[data-prog] i')];
    const desk = W.matchMedia('(min-width: 821px)');
    let cur = 0;
    root.classList.add('is-live');
    const set = (n) => {
      if (n === cur) return;
      cur = n;
      grps.forEach((g, i) => g.classList.toggle('is-on', i === n));
      imgs.forEach((im, i) => {
        if (i === n) im.loading = 'eager';
        im.classList.toggle('is-on', i === n);
      });
      bars.forEach((b, i) => b.classList.toggle('is-on', i <= n));
    };
    on(root, (v) => {
      if (!desk.matches) return -1;
      const mid = v.h * 0.5;
      let best = 0;
      grps.forEach((g, i) => {
        if (g.getBoundingClientRect().top < mid) best = i;
      });
      return best;
    }, (best) => {
      if (best >= 0) set(best);
      return false;
    });
    /* phone: the groups are a snap row; the card in front is the active one */
    snapnav(root.querySelector('.uk-ingr__groups'), root.querySelector('[data-snapnav]'), grps, (k) => {
      if (!desk.matches) set(k);
    });
  };

  /* ---------- Matriarchs: portrait plane scales, title slides behind the figure ---------- */
  const matriarchs = (root) => {
    const title = root.querySelector('.uk-mat__title');
    /* wide screens: sets of three, walking the block order three at a time and wrapping ([1 2 3] → [4 5 6] → [7 1 2] …),
       the whole set flipping at once (right to left, 220 ms apart) and the title behind the centre woman. Up to 820 px only
       the centre panel shows, so it steps one woman at a time in the same order. mi = the first woman on screen. */
    const panels = [...root.querySelectorAll('.uk-mat__panel')];
    const fins = [...root.querySelectorAll('.uk-mat__fin')];
    const n = +root.dataset.n || 1;
    const bar = root.querySelector('[data-bar]');
    const btn = root.querySelector('[data-pause]');
    const one = W.matchMedia('(max-width: 820px)');
    const wrap = (k) => ((k % n) + n) % n;
    let mi = 0;
    let mt = null;
    let paused = still();
    let started = false;
    const run = () => {
      bar.classList.remove('is-run');
      void bar.offsetWidth;
      if (!paused) bar.classList.add('is-run');
    };
    const eager = (ks) => panels.forEach((p) => p.querySelectorAll('img').forEach((im) => ks.includes(+im.dataset.k) && (im.loading = 'eager')));
    const show = (k, cascade) => {
      mi = wrap(k);
      const want = (one.matches ? [mi - 1, mi, mi + 1] : [mi, mi + 1, mi + 2]).map(wrap);
      [2, 1, 0].forEach((slot, order) => {
        const flip = () => {
          panels[slot].querySelectorAll('img').forEach((im) => {
            const on = +im.dataset.k === want[slot];
            if (on) im.loading = 'eager';
            im.classList.toggle('is-on', on);
          });
          if (slot === 1) fins.forEach((f) => f.classList.toggle('is-on', +f.dataset.k === want[1]));
        };
        if (cascade) setTimeout(flip, order * 220);
        else flip();
      });
      /* warm the next step's portraits */
      eager((one.matches ? [mi + 1] : [mi + 3, mi + 4, mi + 5]).map(wrap));
    };
    const go = (k) => {
      show(k, true);
      run();
    };
    const step = () => go(mi + (one.matches ? 1 : 3));
    /* the narrow layout starts on the first woman (the markup's centre is the first set's centre); crossing 820 px
       re-shows the current position in the other layout */
    if (!still() && one.matches) show(0, false);
    one.addEventListener?.('change', () => show(mi, false));
    /* the timer only steps while the section is on screen and the tab is visible */
    let vis = false;
    if ('IntersectionObserver' in W) new IntersectionObserver((es) => (vis = es[0].isIntersecting)).observe(root);
    else vis = true;
    const play = () => {
      clearInterval(mt);
      mt = setInterval(() => vis && !d.hidden && step(), one.matches ? 7200 : 9000);
      run();
    };
    const stop = () => {
      clearInterval(mt);
      mt = null;
      bar.classList.remove('is-run');
    };
    btn.addEventListener('click', () => {
      paused = !paused;
      btn.setAttribute('aria-pressed', paused);
      btn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      paused ? stop() : play();
    });
    if (paused) {
      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('aria-label', 'Play slideshow');
      root.classList.add('is-in');
    }
    io([root], () => {
      root.classList.add('is-in');
      if (!started && !paused) {
        started = true;
        /* warm the first two sets (and the narrow layout's first steps) */
        eager([0, 1, 2, 3, 4, 5].map(wrap));
        play();
      }
    });
    /* phone: the figure cut-out (a CSS mask, so never lazy) is only shown, and fetched, once the section is near */
    io([root], () => root.classList.add('is-near'), { rootMargin: '900px 0px' });
    if (still()) return;
    /* scheduler; --ms is written on the images that scale (the centre portraits and the cut-outs), not the section */
    const scl = [...root.querySelectorAll('.uk-mat__panel--m img.uk-mat__base')].concat(fins);
    on(root, () => root.getBoundingClientRect(), (r, v) => {
      const vh = v.h;
      if (r.bottom < 0 || r.top > vh) return false;
      const t = clamp((vh - r.top) / (vh + r.height), 0, 1);
      const desk = v.w > 820;
      const ph = phone.matches;
      const ms = (desk ? 1.25 - 0.06 * t : ph ? 1.12 - 0.12 * t : 1.08 - 0.08 * t).toFixed(4);
      scl.forEach((el) => el.style.setProperty('--ms', ms));
      /* phones: the title holds still and whole (round 4: no slide past the edge) */
      title.style.setProperty('--tx', (desk ? (0.82 - t) * 140 : 0).toFixed(1) + 'px');
      return false;
    });
  };

  /* ---------- journal (Shapes blog-posts), phone only: a snap row whose images wipe open as they arrive ---------- */
  const journal = (ul) => {
    if (!phone.matches || still()) return;
    ul.classList.add('uk-mj');
    io([...ul.children], (li) => li.classList.add('is-in'), { threshold: 0.3 });
    io([ul], () => ul.scrollLeft < 4 && ul.classList.add('is-nudge'), { threshold: 0.6 });
  };

  const bind = (sel, key, fn) => d.querySelectorAll(sel).forEach((el) => once(el, key) && fn(el));
  const sections = () => {
    bind('[data-uk-hero]', 'hero', hero);
    bind('[data-uk-peak]', 'peak', peak);
    bind('[data-uk-quotes]', 'quotes', quotes);
    bind('[data-uk-feat]', 'feat', feature);
    bind('[data-uk-ingr]', 'ingr', ingredients);
    bind('[data-uk-mat]', 'mat', matriarchs);
    bind('[id$="__ukoo_journal"] ul', 'mjour', journal);
    addButtons(d);
    kick();
  };
  W.UKOO = W.UKOO || {};
  W.UKOO.sections = sections;
  W.UKOO.kit = { bind, renders, on, vp, resizers, kick, io, once, clamp, still, fine, eIO, eOut, seg, lerp };
  /* the Judge.me popup steps aside while a pinned stage plays. Measured once per frame at the frame's start (frame()
     calls holdNow before any section writes), so the stages' layout reads never force a style recalc. */
  holdNow = () => [...d.querySelectorAll('[data-uk-peak], [data-uk-label]')].some((el) => el.__ukHold && el.__ukHold());
  W.UKOO.hold = () => holdV;
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', sections);
  else sections();
})();

/* ==========================================================================
   Stage 2 product page modules: product hero planes + lightbox, Read the label
   camera, Refill Ritual selector, ritual kit, skin feel, ritual closing line.
   ========================================================================== */
(() => {
  const W = window;
  const d = document;
  const html = d.documentElement;
  const K = W.UKOO && W.UKOO.kit;
  if (!K) return;
  const { bind, on, resizers, kick, io, clamp, still, fine, eIO } = K;
  const mobile = W.matchMedia('(max-width: 820px)');
  /* mobile pass: phone-only behaviour (the desktop paths below never read this) */
  const phone = W.matchMedia('(max-width: 749px)');
  const ramp = (p, a, b) => clamp((p - a) / (b - a), 0, 1);
  const money = (c) => {
    const n = c / 100;
    return '$' + (c % 100 ? n.toFixed(2) : String(n));
  };
  const addToCart = async (id, qty, plan) => {
    const item = { id: +id, quantity: qty || 1 };
    if (plan) item.selling_plan = +plan;
    const res = await fetch((W.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    if (!res.ok) throw new Error('add failed');
    d.dispatchEvent(new CustomEvent('theme:update:cart'));
    setTimeout(() => d.dispatchEvent(new CustomEvent('theme:open:cart-drawer')), 350);
  };

  /* ---------- product hero: entrance, planes, lightbox ---------- */
  const phero = (root) => {
    const lcp = root.querySelector('[data-lcp]');
    const go = () => root.classList.add('is-go');
    if (!lcp || still()) go();
    else if (lcp.complete && lcp.naturalWidth) (lcp.decode ? lcp.decode() : Promise.resolve()).then(go, go);
    else {
      lcp.addEventListener('load', () => (lcp.decode ? lcp.decode() : Promise.resolve()).then(go, go));
      lcp.addEventListener('error', go);
      setTimeout(go, 2500);
    }
    const planes = [...root.querySelectorAll('[data-depth]')];
    let mx = 0;
    let my = 0;
    let tx = 0;
    let ty = 0;
    const place = (rh, v) => {
      const y = v.y;
      if (y > rh + 200) return false;
      /* phones keep the scroll depth (no pointer there); tablets stay still */
      const s = still() || (mobile.matches && !phone.matches);
      planes.forEach((el) => {
        const dd = +el.dataset.depth;
        const sy = s ? 0 : y * (dd < 0 ? 0.22 : dd > 0.9 ? -0.1 : -0.02);
        el.style.transform = `translate3d(${(s ? 0 : mx * dd * 12).toFixed(2)}px,${((s ? 0 : my * dd * 8) + sy).toFixed(2)}px,0)`;
      });
      mx += (tx - mx) * 0.07;
      my += (ty - my) * 0.07;
      return Math.abs(tx - mx) > 0.002 || Math.abs(ty - my) > 0.002;
    };
    /* observed on <html> so it runs on every frame like before: a jump back to the top must not paint one stale frame */
    on(html, () => root.offsetHeight, place);
    if (fine && !still()) {
      root.addEventListener('pointermove', (e) => {
        const r = root.getBoundingClientRect();
        tx = -((e.clientX - r.left) / r.width - 0.5);
        ty = -((e.clientY - r.top) / r.height - 0.5);
        kick();
      });
    }
    const lbx = root.querySelector('[data-lbx]');
    if (!lbx) return;
    /* mobile: the photo strip sits at the end of the buy box, as in the mock-up */
    const strip = root.querySelector('.uk-phero__mthumbs');
    const dockEl = root.closest('.shopify-section') && root.closest('.shopify-section').nextElementSibling;
    const dockContent = dockEl && dockEl.querySelector('.product-content');
    const imgs = [...lbx.querySelectorAll('[data-stage] img')];
    const line = lbx.querySelector('.uk-lbx__line i');
    const stage = lbx.querySelector('[data-stage]');
    /* the viewer moves to <body>, so no section stacking context, the dock or the header can ever sit over it; the photo
       sits in a frame sized to its own ratio, and the close and arrow buttons sit inside that frame */
    const frame = d.createElement('div');
    frame.className = 'uk-lbx__frame';
    stage.before(frame);
    frame.append(stage, ...lbx.querySelectorAll('.uk-lbx__x, .uk-lbx__prev, .uk-lbx__next, .uk-lbx__line'));
    if (root.id) {
      d.querySelectorAll(`body > [data-lbx][data-owner="${root.id}"]`).forEach((o) => o.remove());
      lbx.dataset.owner = root.id;
    }
    d.body.appendChild(lbx);
    const multi = imgs.length > 1;
    lbx.classList.toggle('is-single', !multi);
    const fit = (im) => {
      if (im.naturalWidth) frame.style.setProperty('--ar', (im.naturalWidth / im.naturalHeight).toFixed(4));
    };
    let li = 0;
    let last = null;
    const show = (i) => {
      li = (i + imgs.length) % imgs.length;
      imgs.forEach((im, j) => {
        if (j === li) {
          if (!im.src) im.src = im.dataset.src;
          if (im.complete) fit(im);
          else im.addEventListener('load', () => imgs[li] === im && fit(im), { once: true });
        }
        im.classList.toggle('is-on', j === li);
      });
      line.style.transform = `translateX(${li * 100}%)`;
    };
    const open = (i, btn) => {
      last = btn || d.activeElement;
      show(i);
      lbx.classList.add('is-open');
      lbx.setAttribute('aria-hidden', 'false');
      html.style.overflow = 'hidden';
      d.body.style.overflow = 'hidden';
      setTimeout(() => lbx.querySelector('.uk-lbx__x').focus(), 40);
    };
    const close = () => {
      if (!lbx.classList.contains('is-open')) return;
      lbx.classList.remove('is-open');
      lbx.setAttribute('aria-hidden', 'true');
      html.style.overflow = '';
      d.body.style.overflow = '';
      if (last && last.isConnected) last.focus({ preventScroll: true });
    };
    root.querySelectorAll('[data-lb]').forEach((b) => b.addEventListener('click', () => open(+b.dataset.lb, b)));
    if (strip && dockContent) {
      dockContent.appendChild(strip);
      strip.classList.add('is-moved');
    }
    lbx.querySelector('.uk-lbx__x').addEventListener('click', close);
    lbx.querySelector('.uk-lbx__prev').addEventListener('click', () => show(li - 1));
    lbx.querySelector('.uk-lbx__next').addEventListener('click', () => show(li + 1));
    /* a click on the backdrop (outside the photo) closes */
    lbx.addEventListener('click', (e) => (e.target === lbx || e.target === stage) && close());
    d.addEventListener('keydown', (e) => {
      if (!lbx.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight' && multi) show(li + 1);
      else if (e.key === 'ArrowLeft' && multi) show(li - 1);
      else if (e.key === 'Tab') {
        const f = [...lbx.querySelectorAll('button')].filter((b) => b.offsetParent);
        const a = f.indexOf(d.activeElement);
        e.preventDefault();
        f[(a + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      }
    });
    let x0 = null;
    lbx.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
    lbx.addEventListener('touchend', (e) => {
      if (x0 === null || !multi) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) show(li + (dx < 0 ? 1 : -1));
      x0 = null;
    }, { passive: true });
  };

  /* ---------- Read the label: pinned camera over the printed label ---------- */
  const label = (track) => {
    const sec = track.closest('.uk-lbl');
    if (still()) {
      sec.classList.add('is-static');
      return;
    }
    const IW = +track.dataset.iw;
    const IH = +track.dataset.ih;
    const pct = (s) => String(s).split(',').map((x) => parseFloat(x));
    const toBox = (a) => [(a[0] * IW) / 100, (a[1] * IH) / 100, ((a[0] + a[2]) * IW) / 100, ((a[1] + a[3]) * IH) / 100];
    const stage = track.querySelector('[data-stage]');
    const cam = track.querySelector('[data-cam]');
    const intro = track.querySelector('[data-intro]');
    const outL = [...track.querySelectorAll('[data-out] li')];
    const prog = track.querySelector('[data-prog]');
    const progB = [...prog.querySelectorAll('b')];
    const S = [...track.querySelectorAll('.uk-lbl__note')].map((n) => ({
      k: n.dataset.k,
      box: toBox(pct(n.dataset.box)),
      side: n.dataset.side === 'l' ? 'l' : 'r',
      zoom: parseFloat(n.dataset.zoom) || 1.15,
      tight: n.dataset.tight === 'true',
      note: n,
      dim: track.querySelector(`.uk-lbl__dim[data-k="${n.dataset.k}"]`),
      lead: track.querySelector(`.uk-lbl__lead[data-k="${n.dataset.k}"]`),
    }));
    const BOT = [0, 0, IW, IH];
    const LAB = toBox(pct(track.dataset.label || '15,28,72,64'));
    const n = S.length;
    const gap = 0.065;
    const slot = (0.84 - 0.21 + gap) / Math.max(n, 1);
    const TL = [['intro', 0, 0.035], ['label', 0.11, 0.14]];
    S.forEach((s, i) => TL.push([i, 0.21 + i * slot, 0.21 + i * slot + slot - gap]));
    TL.push(['end', 0.93, 1]);
    const SMAX = parseFloat(track.dataset.smax) || 0.8;
    const KEY = {};
    let vw = 0;
    let vh = 0;
    let mob = false;
    let sheetTop = 0;
    let sheet = false;
    const ctr = (b) => [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
    const frame = (b, X, Y, zw, zh, smax) => {
      const c = ctr(b);
      return { cx: c[0], cy: c[1], X, Y, s: Math.min(zw / (b[2] - b[0]), zh / (b[3] - b[1]), smax, SMAX) };
    };
    const layout = () => {
      vw = stage.clientWidth;
      vh = stage.clientHeight;
      mob = mobile.matches;
      if (!mob) {
        KEY.intro = frame(BOT, vw * 0.68, vh * 0.53, 9999, vh * 0.8, 1);
        KEY.label = frame(LAB, vw * 0.5, vh * 0.5, 9999, vh * 0.92, 1);
        S.forEach((s, i) => (KEY[i] = frame(s.box, s.side === 'r' ? vw * 0.335 : vw * 0.665, vh * 0.5, vw * 0.5, vh * 0.62, s.zoom)));
        KEY.end = frame(BOT, vw * 0.3, vh * 0.53, 9999, vh * 0.8, 1);
      } else {
        KEY.intro = frame(BOT, vw * 0.5, vh * 0.62, 9999, vh * 0.5, 1);
        KEY.label = frame(LAB, vw * 0.5, vh * 0.5, vw * 0.9, vh * 0.8, 1);
        S.forEach((s, i) => (KEY[i] = frame(s.box, vw * 0.5, vh * 0.27, vw * 0.9, vh * 0.32, 1)));
        KEY.end = frame(BOT, vw * 0.5, vh * 0.32, 9999, vh * 0.48, 1);
        sheet = false;
        if (phone.matches) {
          /* phone: the lit line fills the width above a fixed note sheet; the sheet clears the sticky bar
             (its real height, safe area included). Too short a stage (landscape) keeps the plain layout. */
          const barEl = d.querySelector("body > [class*='lg:max-w-3xl'][class*='bottom-0'] > .card");
          const bar = Math.max(72, barEl ? barEl.offsetHeight : 0);
          S.forEach((s, i) => {
            s.note.dataset.n = String(i + 1).padStart(2, '0');
            s.note.dataset.of = String(n).padStart(2, '0');
          });
          const nh = Math.max(120, ...S.map((s) => s.note.offsetHeight));
          sheetTop = Math.round(vh - bar - nh - 56);
          sheet = sheetTop - 104 > 160;
          stage.style.setProperty('--sheet-top', sheetTop + 'px');
          stage.style.setProperty('--sheet', '0');
          if (sheet) S.forEach((s, i) => (KEY[i] = frame(s.box, vw * 0.5, (64 + sheetTop - 40) / 2, vw * 0.92, (sheetTop - 104) * 0.62, 1)));
        }
      }
      S.forEach((s, i) => {
        const f = s.tight ? (mob ? 8 : 14) : mob ? 28 : 44;
        const Kf = KEY[i];
        const b = s.box;
        const px = mob ? 12 : 22;
        const py = s.tight ? (mob ? 2 : 4) : mob ? 10 : 18;
        const r = { l: Kf.X + (b[0] - Kf.cx) * Kf.s - px, r: Kf.X + (b[2] - Kf.cx) * Kf.s + px, t: Kf.Y + (b[1] - Kf.cy) * Kf.s - py, b: Kf.Y + (b[3] - Kf.cy) * Kf.s + py };
        const ds = s.dim.style;
        ds.setProperty('--l', r.l + 'px');
        ds.setProperty('--r', r.r + 'px');
        ds.setProperty('--t', r.t + 'px');
        ds.setProperty('--b', r.b + 'px');
        ds.setProperty('--f', f + 'px');
        const N = s.note;
        const L = s.lead;
        if (!mob) {
          const nw = Math.min(430, vw * 0.31);
          const g = 48;
          let nx = s.side === 'r' ? Math.max(r.r + g + 56, vw * 0.62) : Math.min(r.l - g - 56 - nw, vw * 0.38 - nw);
          nx = clamp(nx, Math.min(56, vw * 0.039), vw - nw - Math.min(56, vw * 0.039));
          N.style.left = nx + 'px';
          N.style.top = vh * 0.5 + 'px';
          N.dataset.base = 'translateY(-50%)';
          L.className = 'uk-lbl__lead ' + (s.side === 'r' ? 'is-fl' : 'is-fr');
          const x1 = s.side === 'r' ? r.r - 8 : nx + nw + g * 0.4;
          const x2 = s.side === 'r' ? nx - g * 0.4 : r.l + 8;
          L.style.cssText = `left:${x1}px;top:${vh * 0.5}px;width:${Math.max(0, x2 - x1)}px;transform-origin:${s.side === 'r' ? 'left' : 'right'}`;
        } else if (sheet) {
          N.style.left = '';
          N.style.top = sheetTop + 28 + 'px';
          N.dataset.base = 'translateY(0)';
          L.className = 'uk-lbl__lead is-v';
          L.style.cssText = `left:${vw * 0.5}px;top:${r.b + 6}px;height:${Math.max(0, sheetTop - r.b - 10)}px`;
        } else {
          const top = Math.max(r.b + 28, vh * 0.45);
          N.style.left = '';
          N.style.top = top + 'px';
          N.dataset.base = 'translateY(0)';
          L.className = 'uk-lbl__lead is-v';
          L.style.cssText = `left:${vw * 0.5}px;top:${r.b + 6}px;height:${Math.max(0, top - r.b - 18)}px`;
        }
      });
    };
    const camAt = (p) => {
      for (let i = 0; i < TL.length; i++) {
        const nn = TL[i];
        if (p >= nn[1] && p <= nn[2]) return KEY[nn[0]];
        if (i < TL.length - 1 && p > nn[2] && p < TL[i + 1][1]) {
          const A = KEY[nn[0]];
          const B = KEY[TL[i + 1][0]];
          const t = eIO((p - nn[2]) / (TL[i + 1][1] - nn[2]));
          const dist = Math.hypot(B.cx - A.cx, B.cy - A.cy) * (1985 / IW);
          const dip = dist > 700 ? 0.16 : dist > 250 ? 0.08 : 0;
          const sc = Math.exp(Math.log(A.s) + (Math.log(B.s) - Math.log(A.s)) * t) * (1 - dip * Math.sin(Math.PI * t));
          return { cx: A.cx + (B.cx - A.cx) * t, cy: A.cy + (B.cy - A.cy) * t, X: A.X + (B.X - A.X) * t, Y: A.Y + (B.Y - A.Y) * t, s: sc };
        }
      }
      return KEY.end;
    };
    let pT = 0;
    let pS = 0;
    let mxT = 0;
    let myT = 0;
    let mxS = 0;
    let myS = 0;
    const progress = () => {
      const r = track.getBoundingClientRect();
      return clamp(-r.top / Math.max(1, track.offsetHeight - vh), 0, 1);
    };
    const render = () => {
      const p = pS;
      const st = camAt(p);
      let lit = 0;
      S.forEach((s, i) => {
        const h = TL[i + 2];
        const a = h[1];
        const b = h[2];
        const dIn = ramp(p, a - 0.018, a + 0.012);
        const dOut = ramp(p, b - 0.006, b + 0.022);
        const dv = dIn * (1 - dOut);
        lit = Math.max(lit, dv);
        s.dim.style.opacity = dv.toFixed(3);
        const nIn = ramp(p, a - 0.004, a + 0.024);
        const nOut = ramp(p, b - 0.014, b + 0.008);
        const dir = mob ? 0 : s.side === 'r' ? 1 : -1;
        s.note.style.opacity = (nIn * (1 - nOut)).toFixed(3);
        s.note.style.transform = `${s.note.dataset.base} translate3d(${dir * (1 - nIn) * 36 - dir * nOut * 14}px,${mob ? (1 - nIn) * 18 : 0}px,0)`;
        const lv = ramp(p, a + 0.002, a + 0.03) * (1 - nOut);
        s.lead.style.transform = (mob ? 'scaleY(' : 'scaleX(') + lv.toFixed(3) + ')';
        progB[i].style.transform = 'scaleX(' + ramp(p, a - 0.06, b).toFixed(3) + ')';
      });
      const par = (1 - lit) * (mob ? 0 : 1);
      const tx = st.X - st.s * st.cx + mxS * 8 * par;
      const ty = st.Y - st.s * st.cy + myS * 6 * par;
      cam.style.transform = `translate3d(${tx.toFixed(2)}px,${ty.toFixed(2)}px,0) scale(${st.s.toFixed(5)})`;
      const iv = 1 - ramp(p, 0.02, 0.075);
      intro.style.opacity = iv.toFixed(3);
      intro.style.transform = (mob ? '' : 'translateY(-50%) ') + `translate3d(${-mxS * 4 * par}px,${(1 - iv) * -40}px,0)`;
      prog.style.opacity = (ramp(p, 0.12, 0.17) * (1 - ramp(p, 0.85, 0.9))).toFixed(3);
      if (mob && sheet && n) stage.style.setProperty('--sheet', (ramp(p, TL[2][1] - 0.03, TL[2][1]) * (1 - ramp(p, TL[n + 1][2], TL[n + 1][2] + 0.03))).toFixed(3));
      outL.forEach((li, i) => {
        const v = ramp(p, 0.885 + i * 0.018, 0.93 + i * 0.018);
        li.style.opacity = v.toFixed(3);
        li.style.transform = `translate3d(0,${((1 - v) * 22).toFixed(1)}px,0)`;
      });
    };
    /* one easing step per scheduler frame; true asks for another frame while the camera or the pointer settles */
    const tick = () => {
      const dd = pT - pS;
      pS += Math.abs(dd) < 0.0004 ? dd : dd * (mob ? 0.2 : 0.13);
      mxS += (mxT - mxS) * 0.08;
      myS += (myT - myS) * 0.08;
      render();
      return Math.abs(pT - pS) > 0.00005 || Math.abs(mxT - mxS) > 0.002 || Math.abs(myT - myS) > 0.002;
    };
    /* the high-resolution bottle loads only as the stage approaches; on a phone the stage starts near the fold, so
       it also waits for the page's load event, keeping the large file out of the first paint's bandwidth */
    const camImg = cam.querySelector('[data-cam-img]');
    const camLoad = () => io([track], () => (camImg.src = camImg.dataset.src), { rootMargin: '120% 0px 120% 0px' });
    if (camImg && !camImg.src) {
      if (phone.matches && d.readyState !== 'complete') W.addEventListener('load', camLoad, { once: true });
      else camLoad();
    }
    sec.classList.add('is-live');
    layout();
    pT = pS = progress();
    render();
    resizers.add(() => {
      layout();
      pT = progress();
      render();
    });
    /* read: the track's place; write: ease toward it. Off screen the camera jumps to its target (nothing to see). */
    on(track, (v) => {
      const r = track.getBoundingClientRect();
      return { p: clamp(-r.top / Math.max(1, track.offsetHeight - vh), 0, 1), off: r.bottom < 0 || r.top > v.h };
    }, (s) => {
      pT = s.p;
      if (s.off) pS = pT;
      return tick();
    }, '50% 0px');
    if (fine) {
      stage.addEventListener('pointermove', (e) => {
        mxT = (e.clientX / vw - 0.5) * 2;
        myT = (e.clientY / vh - 0.5) * 2;
        kick();
      });
    }
    track.__ukHold = () => {
      const r = track.getBoundingClientRect();
      return r.top < W.innerHeight * 0.5 && r.bottom > W.innerHeight * 0.5;
    };
  };

  /* ---------- Refill Ritual selector (posts the native selling_plan) ---------- */
  const po = (root) => {
    const form = d.getElementById(root.dataset.form);
    const sp = root.querySelector('[data-sp]');
    const wrap = root.querySelector('[data-tiers]');
    const base = +root.dataset.base;
    const modes = [...root.querySelectorAll('input[value="once"], input[value="sub"]')];
    const tiers = [...root.querySelectorAll('[data-tiers] input')];
    const sectionEl = root.closest('.shopify-section') || d;
    const state = () => {
      const sub = (modes.find((m) => m.checked) || {}).value === 'sub';
      const t = tiers.find((x) => x.checked) || tiers[0];
      return { sub, plan: sub && t ? t.value : '', price: sub && t ? +t.dataset.price : base, weeks: sub && t ? t.dataset.weeks : '' };
    };
    const sync = () => {
      const s = state();
      wrap.classList.toggle('is-open', s.sub);
      wrap.inert = !s.sub;
      tiers.forEach((t) => t.removeAttribute('tabindex'));
      sp.value = s.plan;
      /* keep every selling_plan field the form owns (ours and the app's hidden ones) in step at all times,
         so express checkout buttons read the same choice as Add to bag */
      if (form) [...form.elements].forEach((el) => el.name === 'selling_plan' && (el.value = s.plan));
      root.classList.toggle('is-sub', s.sub);
      sectionEl.querySelectorAll('[data-uk-price]').forEach((el) => (el.textContent = money(s.price)));
      d.querySelectorAll("body > [class*='bottom-0'] [data-product-price] > span:not(.sr-only)").forEach((el) => (el.textContent = money(s.price)));
      W.dispatchEvent(new CustomEvent('ukoo:plan', { detail: s }));
    };
    /* mirror the chosen price in the Shapes price block and on the Add to bag button */
    const priceEl = sectionEl.querySelector('.product-block-price > span:not(.sr-only)');
    if (priceEl) priceEl.setAttribute('data-uk-price', '');
    const qtyIn = form ? d.querySelector(`input[name="quantity"][form="${form.id}"]`) || form.querySelector('input[name="quantity"]') : null;
    const btnLabel = form ? form.querySelector('.add-to-cart-btn .push-btn__surface > span') : null;
    let btnPrice = null;
    if (btnLabel) {
      btnPrice = d.createElement('span');
      btnPrice.className = 'uk-btn-price';
      btnLabel.appendChild(btnPrice);
    }
    const paint = () => {
      if (!btnPrice) return;
      const q = Math.max(1, parseInt(qtyIn && qtyIn.value, 10) || 1);
      btnPrice.textContent = ' · ' + money(state().price * q);
    };
    W.addEventListener('ukoo:plan', paint);
    if (qtyIn) {
      const qb = qtyIn.closest('.product-block') || qtyIn.parentElement;
      qb.addEventListener('click', () => setTimeout(paint, 0));
      qtyIn.addEventListener('input', paint);
      qtyIn.addEventListener('change', paint);
    }
    modes.concat(tiers).forEach((r) => r.addEventListener('change', sync));
    if (form) {
      /* every selling_plan field the form owns (ours and the app's hidden ones) carries the same value at submit */
      const align = (e) => {
        if (e.target !== form) return;
        const v = state().plan;
        [...form.elements].forEach((el) => {
          if (el.name === 'selling_plan') el.value = v;
        });
      };
      d.addEventListener('submit', align, true);
    }
    sync();
  };

  /* ---------- ritual kit: swap the wash, add the chosen kit ---------- */
  const kit = (root) => {
    const radios = [...root.querySelectorAll('input[type="radio"]')];
    const box = root.querySelector('[data-wash]');
    const img = root.querySelector('[data-wash-img]');
    const items = root.querySelector('[data-items]');
    const btn = root.querySelector('[data-kit-add]');
    const lot = root.querySelector('[data-lot] img');
    radios.forEach((r) =>
      r.addEventListener('change', () => {
        if (!r.checked) return;
        if (btn) btn.dataset.kitAdd = r.value;
        if (items && lot) items.textContent = r.dataset.name + ' ' + items.dataset.and + ' ' + lot.alt;
        const pre = new Image();
        pre.src = r.dataset.src;
        /* phone: the wash slides out one side and in from the other, in the direction of the choice */
        const ph = phone.matches;
        if (ph) box.style.setProperty('--uk-dir', radios.indexOf(r) > 0 ? -1 : 1);
        box.classList.add('is-swap');
        setTimeout(() => {
          img.src = r.dataset.src;
          img.alt = r.dataset.name;
          if (ph) box.classList.add('is-enter');
          const done = ph ? () => requestAnimationFrame(() => requestAnimationFrame(() => box.classList.remove('is-swap', 'is-enter'))) : () => box.classList.remove('is-swap');
          (pre.decode ? pre.decode() : Promise.resolve()).then(done, done);
        }, still() ? 0 : 300);
      })
    );
    /* phone: swipe the stage to switch between the two kits */
    const stg = root.querySelector('[data-stage]');
    if (phone.matches && radios.length > 1 && stg) {
      let x0 = null;
      let y0 = 0;
      stg.addEventListener('touchstart', (e) => {
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      }, { passive: true });
      stg.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (!phone.matches || Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        const i = radios.findIndex((x) => x.checked);
        const j = clamp(i + (dx < 0 ? 1 : -1), 0, radios.length - 1);
        if (j === i) return;
        radios[j].checked = true;
        radios[j].dispatchEvent(new Event('change', { bubbles: true }));
      }, { passive: true });
    }
    if (btn) {
      const lab = btn.innerHTML;
      btn.addEventListener('click', async () => {
        if (btn.getAttribute('aria-busy') === 'true') return;
        btn.setAttribute('aria-busy', 'true');
        try {
          await addToCart(btn.dataset.kitAdd, 1);
          btn.textContent = btn.dataset.done;
          setTimeout(() => (btn.innerHTML = lab), 1800);
        } catch (e) {
          btn.innerHTML = lab;
        } finally {
          btn.removeAttribute('aria-busy');
        }
      });
    }
    const stage = root.querySelector('[data-stage]');
    const bw = root.querySelector('[data-wash]');
    const bl = root.querySelector('[data-lot]');
    on(stage, () => (still() ? null : stage.getBoundingClientRect()), (r, v) => {
      let q = 1;
      if (r) {
        const vh = v.h;
        if (r.bottom < -100 || r.top > vh + 100) return false;
        q = eIO(clamp((vh - r.top) / (vh * 0.62 + r.height * 0.35), 0, 1));
      }
      const D = (mobile.matches ? 0.24 : 0.17) * v.w * (1 - q);
      bw.style.transform = `translate3d(${(-D).toFixed(1)}px,0,0) rotate(${(-(1 - q) * 4).toFixed(2)}deg)`;
      bl.style.transform = `translate3d(${D.toFixed(1)}px,${((1 - q) * -14).toFixed(1)}px,0) rotate(${((1 - q) * 3).toFixed(2)}deg)`;
      return false;
    });
  };

  /* ---------- skin feel words, editorial scale, ritual closing wipe ---------- */
  const feel = (root) => {
    const wls = [...root.querySelectorAll('.uk-wl')];
    const eds = [...root.querySelectorAll('.uk-ed')];
    if (still()) {
      wls.concat(eds).forEach((e) => e.classList.add('is-in'));
      return;
    }
    let q = 0;
    io(wls, (el) => {
      setTimeout(() => el.classList.add('is-in'), q++ * 160);
      setTimeout(() => (q = Math.max(0, q - 1)), 160);
    }, { rootMargin: '0px 0px -18% 0px', threshold: 0.6 });
    io(eds, (el) => el.classList.add('is-in'), { threshold: 0.15 });
    /* phone: the two photos drift at different depths as the pair scrolls through */
    const ims = root.querySelector('.uk-feel__ims');
    const f1 = root.querySelector('.uk-feel__f1');
    const f2 = root.querySelector('.uk-feel__f2');
    if (phone.matches && ims && f1 && f2) {
      on(ims, () => (phone.matches ? ims.getBoundingClientRect() : null), (r, v) => {
        if (!r) {
          /* rotated or resized past the phone width: the desktop layout gets its photos back untouched */
          if (f1.style.transform) f1.style.transform = f2.style.transform = '';
          return false;
        }
        const vh = v.h;
        if (r.bottom < -60 || r.top > vh + 60) return false;
        const q = clamp((vh - r.top) / (vh + r.height), 0, 1) - 0.5;
        f1.style.transform = `translate3d(0,${(q * -40).toFixed(1)}px,0)`;
        f2.style.transform = `translate3d(0,${(q * 60).toFixed(1)}px,0)`;
        return false;
      });
    }
  };
  const harmony = (root) => {
    const c = root.querySelector('[data-hwipe]');
    if (!c || still()) return;
    on(c, () => c.getBoundingClientRect(), (r, v) => {
      const vh = v.h;
      if (r.top > vh || r.bottom < 0) return false;
      const q = clamp((vh * 0.96 - r.top) / (vh * 0.5), 0, 1);
      c.style.setProperty('--hw', ((1 - eIO(q)) * 100).toFixed(2) + '%');
      return false;
    });
  };

  /* Judge.me popup steps aside while a pinned stage plays (the hold is measured at the frame's start; this only writes) */
  on(html, () => !!(W.UKOO.hold && W.UKOO.hold()), (h) => {
    html.classList.toggle('uk-hold', h);
    return false;
  });

  /* ---------- phone: whole-dollar prices; the sticky bar shows the short name and the chosen plan ---------- */
  let plan = null; /* the last Refill Ritual choice (ukoo:plan detail) */
  const BAR = "body > [class*='lg:max-w-3xl'][class*='bottom-0']";
  const cents = (t) => Math.round(parseFloat(String(t).replace(/[^0-9.]/g, '')) * 100) || 0;
  /* every text the phone rewrites keeps its original (re-taken whenever the theme has rewritten it since, e.g. on a
     variant change), so a resize past the phone width puts the desktop text back */
  const own = (el, txt) => {
    if (!('ukOrig' in el.dataset) || el.textContent !== el.dataset.ukSet) el.dataset.ukOrig = el.textContent;
    el.textContent = el.dataset.ukSet = typeof txt === 'function' ? txt(el.dataset.ukOrig) : txt;
  };
  const sticky = (e) => {
    if (e && e.detail) plan = e.detail;
    if (!phone.matches) return;
    let unit = 0;
    d.querySelectorAll('.product-block-price > span:not(.sr-only)').forEach((el) => {
      if (el.hasAttribute('data-uk-price')) return;
      own(el, (t) => t.replace(/\.00\b/, '').trim());
      /* re-read the unit price every time: the theme rewrites this block on a variant change */
      unit = unit || cents(el.dataset.ukOrig);
    });
    const bar = d.querySelector(BAR);
    if (!bar) return;
    const nm = bar.querySelector('.font-heading');
    if (nm) own(nm, (t) => t.split(/\s[-–]\s/)[0].trim());
    const pr = bar.querySelector('[data-product-price] > span:not(.sr-only)');
    if (pr && !unit) unit = cents(pr.textContent !== pr.dataset.ukSet ? pr.textContent : pr.dataset.ukOrig);
    const each = plan ? plan.price : unit;
    if (!each) return;
    if (pr) own(pr, plan && plan.sub && plan.weeks ? `${money(each)} · every ${plan.weeks} weeks` : money(each));
    const lab = bar.querySelector('.add-to-cart-btn .push-btn__surface > span');
    if (lab) {
      const qIn = d.querySelector('.shopify-section:has(.uk-phero) + .shopify-section input[name="quantity"]');
      const q = Math.max(1, parseInt(qIn && qIn.value, 10) || 1);
      let bp = lab.querySelector('.uk-btn-price[data-uk-m]');
      if (!bp) {
        bp = d.createElement('span');
        bp.className = 'uk-btn-price';
        bp.dataset.ukM = '';
        lab.appendChild(bp);
      }
      bp.textContent = ' · ' + money(each * q);
    }
  };
  const unsticky = () => {
    d.querySelectorAll('[data-uk-orig]').forEach((el) => {
      if (el.textContent === el.dataset.ukSet) el.textContent = el.dataset.ukOrig;
      delete el.dataset.ukOrig;
      delete el.dataset.ukSet;
    });
    d.querySelectorAll('.uk-btn-price[data-uk-m]').forEach((el) => el.remove());
  };
  W.addEventListener('ukoo:plan', sticky);
  W.addEventListener('load', () => sticky());
  phone.addEventListener('change', () => (phone.matches ? sticky() : unsticky()));
  d.addEventListener('change', (e) => {
    if (e.target.name === 'quantity') sticky();
    else if (e.target.closest && e.target.closest('.product-block-variant-picker')) setTimeout(sticky, 120);
  });
  d.addEventListener('click', (e) => e.target.closest && e.target.closest('.product-block-quantity-selector') && setTimeout(sticky, 0));

  /* ---------- card-po: the Refill Ritual choice under a product card (snippets/ukoo-card-po.liquid) ----------
     One-time leaves the card's own quick add untouched. With a rhythm chosen, a capture listener on the card
     takes the Add click first and adds the variant with that selling_plan. */
  const cardPo = (root) => {
    const card = root.closest('.uk-card') || root.parentElement;
    const btn = card.querySelector('[data-uk-add]');
    const sel = root.querySelector('[data-cpo-sel]');
    const sub = root.querySelector('input[value="sub"]');
    const priceEl = card.querySelector('.uk-card__price');
    const base = +root.dataset.base;
    if (!sel || !sub) return;
    const state = () => {
      const o = sel.options[sel.selectedIndex];
      return sub.checked && o ? { sub: true, plan: o.value, price: +o.dataset.price } : { sub: false, plan: '', price: base };
    };
    const sync = () => {
      const s = state();
      root.classList.toggle('is-sub', s.sub);
      sel.disabled = !s.sub;
      if (!priceEl) return;
      priceEl.textContent = '';
      if (s.sub) {
        const was = d.createElement('s');
        was.textContent = money(base);
        priceEl.append(was, ' ', money(s.price));
      } else priceEl.textContent = money(base);
    };
    root.addEventListener('change', sync);
    if (btn) {
      card.addEventListener(
        'click',
        async (e) => {
          if (!e.target.closest || e.target.closest('[data-uk-add]') !== btn) return;
          const s = state();
          if (!s.sub) return;
          e.preventDefault();
          e.stopPropagation();
          if (btn.getAttribute('aria-busy') === 'true') return;
          btn.setAttribute('aria-busy', 'true');
          const t = btn.querySelector('span');
          const label = t ? t.textContent : '';
          try {
            await addToCart(btn.dataset.ukAdd, 1, s.plan);
            btn.classList.add('is-done');
            if (t) t.textContent = 'Added';
            setTimeout(() => {
              btn.classList.remove('is-done');
              if (t) t.textContent = label;
            }, 1800);
          } catch (err) {
            if (t) t.textContent = label;
          } finally {
            btn.removeAttribute('aria-busy');
          }
        },
        true
      );
    }
    sync();
  };

  /* ---------- info: "Subscription details" disclosure (snippets/ukoo-sub-info.liquid) ----------
     Click or tap only (Ryan 2026-10-14: hover opening it was distracting): a click, tap, Enter or Space toggles it;
     Esc, a tap outside or focus leaving closes it.
     Event-driven only: no scroll work, no layout reads.
     Phones: the note is a fixed sheet along the bottom edge, but a transformed ancestor (peak cards, the product
     dock) would trap position: fixed inside a narrow card, so while open it lives on <body> and goes home on close. */
  const info = (root) => {
    const btn = root.querySelector('.uk-si__b');
    const pop = root.querySelector('.uk-si__pop');
    if (!btn || !pop) return;
    const home = pop.parentNode;
    const mine = (n) => !!n && (root.contains(n) || pop.contains(n));
    const outside = (e) => !mine(e.target) && set(false);
    const set = (open) => {
      if (open === !pop.hidden) return;
      if (open && phone.matches) d.body.appendChild(pop);
      else if (!open && pop.parentNode !== home) home.appendChild(pop);
      /* wider screens: a trigger low in the window opens the note upwards, so a pinned 100vh stage (home peak) or the
         window edge never cuts it off (one read, on open only) */
      if (open && !phone.matches) root.classList.toggle('is-up', btn.getBoundingClientRect().bottom > W.innerHeight * 0.55);
      pop.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      root.classList.toggle('is-open', open);
      if (open) d.addEventListener('pointerdown', outside, true);
      else d.removeEventListener('pointerdown', outside, true);
    };
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      set(pop.hidden);
    });
    /* crossing the phone width while open (rotation, resize) closes it, so it never strands on <body> or off screen */
    phone.addEventListener('change', () => set(false));
    const out = (e) => !mine(e.relatedTarget) && set(false);
    root.addEventListener('focusout', out);
    pop.addEventListener('focusout', out);
    const esc = (e) => {
      if (e.key !== 'Escape' || pop.hidden) return;
      e.stopPropagation();
      set(false);
      btn.focus({ preventScroll: true });
      set(false);
    };
    root.addEventListener('keydown', esc);
    pop.addEventListener('keydown', esc);
  };

  const run = () => {
    bind('[data-uk-phero]', 'phero', phero);
    bind('[data-uk-label]', 'label', label);
    bind('[data-uk-po]', 'po', po);
    bind('[data-uk-cpo]', 'cpo', cardPo);
    bind('[data-uk-info]', 'info', info);
    bind('[data-uk-kit]', 'kit', kit);
    bind('[data-uk-feat="feel"]', 'feel', feel);
    bind('[data-uk-feat="harmony"]', 'harm', harmony);
    sticky();
    kick();
  };
  run();
  d.addEventListener('shopify:section:load', run);
})();
